import Alert from "@cloudscape-design/components/alert"
import Box from "@cloudscape-design/components/box"
import Button from "@cloudscape-design/components/button"
import Modal from "@cloudscape-design/components/modal"
import SpaceBetween from "@cloudscape-design/components/space-between"
import Textarea from "@cloudscape-design/components/textarea"

/**
 * Modal overlay for electronically signing a DTA.
 */
interface SignModalProps {
  /**
   * Whether the modal is visible.
   */
  open: boolean
  /**
   * Callback to close the modal.
   */
  onClose: () => void
  /**
   * Current value of the optional comment textarea.
   */
  comment: string
  /**
   * Callback when the comment text changes.
   */
  onCommentChange: (value: string) => void
  /**
   * Callback to submit the signature.
   */
  onSubmit: () => void
  /**
   * Whether the sign request is in flight.
   */
  submitting: boolean
  /**
   * Error message from a failed sign attempt, if any.
   */
  error: string | null
}

/**
 * Renders a modal dialog for signing a DTA with an optional comment.
 */
const SignModal = ({
  open,
  onClose,
  comment,
  onCommentChange,
  onSubmit,
  submitting,
  error,
}: SignModalProps) => {
  return (
    <Modal
      visible={open}
      onDismiss={onClose}
      header="Sign DTA"
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={onClose}>
              Cancel
            </Button>
            <Button variant="primary" iconName="edit" onClick={onSubmit} loading={submitting}>
              {submitting ? "Signing..." : "Sign & Approve"}
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="m">
        <Box variant="p">Add your electronic signature to approve this agreement.</Box>
        <Textarea
          value={comment}
          onChange={({ detail }) => onCommentChange(detail.value)}
          placeholder="Optional comment..."
          rows={3}
        />
        {error && <Alert type="error">{error}</Alert>}
      </SpaceBetween>
    </Modal>
  )
}

export default SignModal
